import { Box, List, Section, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import EmployeeLabel from 'modules/surveys/components/Participants/common/EmployeeLabel'
import type Survey from 'modules/surveys/store/models/v2/Survey'

import EditorRightActions from './EditorRightActions'

interface ItemProps {
  supervisorId: number
  survey: Survey
  authorId: number
}

function Item({ supervisorId, survey, authorId }: ItemProps) {
  const {
    access: { id: currentAccessId },
  } = useCurrentSession()
  const supervisor = users.find((user) => user.attributes.id === supervisorId)
  const userIsAuthor = supervisor?.attributes.id === authorId
  const currentUserIsAuthor = userIsAuthor && currentAccessId === authorId

  if (!supervisor) return null

  return (
    <List.Item>
      <Box key={supervisor.attributes.id} paddingY="s16">
        <EmployeeLabel user={supervisor} />
      </Box>
      {!currentUserIsAuthor && (
        <EditorRightActions
          userIsAuthor={userIsAuthor}
          supervisorId={supervisorId}
          survey={survey}
        />
      )}
    </List.Item>
  )
}

interface ItemsProps {
  supervisorsIds: number[]
  survey: Survey
  authorId: number
}

const Items = observer(({ supervisorsIds, survey, authorId }: ItemsProps) => {
  if (!supervisorsIds.length) {
    return (
      <Box aria-label="List of editors">
        <Text.Normal>{i18n.t('surveys.form.managers.blank_slate')}</Text.Normal>
      </Box>
    )
  }

  return (
    <Box aria-label="List of editors">
      <List>
        {supervisorsIds.map((supervisorId) => (
          <Item
            key={supervisorId}
            supervisorId={supervisorId}
            survey={survey}
            authorId={authorId}
          />
        ))}
      </List>
    </Box>
  )
})

interface Props {
  survey: Survey
}

function EditorsSection({ survey }: Props) {
  const { navigation } = useNavigation()
  const authorId = survey.attributes.author_id
  const route = navigation.engagement.surveys.id.configuration.addManager.fullPath({
    id: survey.attributes.id,
  })
  const addEditor = {
    to: route,
    label: i18n.t('surveys.form.managers.add_manager'),
  }

  return (
    <Section
      illustration="people-add"
      title={i18n.t('surveys.form.managers.title')}
      description={i18n.t('surveys.form.managers.description')}
      action={addEditor}
    >
      <Items
        supervisorsIds={survey.attributes.supervisor_ids}
        survey={survey}
        authorId={authorId}
      />
    </Section>
  )
}

export default observer(EditorsSection)
